import { Location, Route } from 'vue-router'

import { LoadSessionData } from '@/services/session/load-session-data'
import { DataFulfilledSessionCookiePayload } from '@/services/session/session-cookie'
import { log } from '@/util/log'
import { buildAbsoluteSpaPathForRedirect } from '@spa/router/hooks/lib/build-absolute-spa-path-for-redirect'

type RouteLoginRequired = Pick<Route, 'meta' | 'path' | 'query' | 'hash' | 'matched'>

interface RequireLoginDep {
  getSessionPayloadWithDataFulfilled: () => Promise<DataFulfilledSessionCookiePayload>
}

type RequireLoginBuilder = (dep: RequireLoginDep)
  => (to: RouteLoginRequired) => Promise<Location | null>

export const buildRequireLogin: RequireLoginBuilder = (dep: RequireLoginDep) => (
  async (to: RouteLoginRequired): Promise<Location | null> => {
    const requiresLogin = to.matched.some(route =>
      route.meta.requireLogin
      || route.meta.requireCartLogin
      || route.meta.requirePaidMembershipLogin
    )

    if (!requiresLogin) {
      return null
    }

    const userIsRegistered = await dep.getSessionPayloadWithDataFulfilled().then(
      (payload: DataFulfilledSessionCookiePayload) => payload.data.isRegistered,
      (e) => {
        log.error('error on load user_status', e)

        return false
      }
    )

    log.debug('requireLogin hook triggered, userIsRegistered = ', userIsRegistered)

    if (userIsRegistered) {
      return null
    }

    return buildRedirectLocation(to)
  }
)

function buildRedirectLocation(to: RouteLoginRequired): Location {
  if (to.matched.some(route => route.meta.requireCartLogin)) {
    return {
      path: '/cart/login',
      query: {
        from: buildAbsoluteSpaPathForRedirect(to.path),
        ...to.query,
      },
      hash: to.hash,
    }
  }

  if (to.matched.some(route => route.meta.requirePaidMembershipLogin)) {
    return {
      path: '/paid-membership/subscribe/login',
      query: to.query,
      hash: to.hash,
    }
  }

  return {
    path: '/signup',
    // vue-router のフックを経由している = SPA内部の遷移なので
    // spa用のパスに変換している
    query: { next: buildAbsoluteSpaPathForRedirect(to.path), ...to.query },
    hash: to.hash,
  }
}

export const requireLogin = buildRequireLogin({
  getSessionPayloadWithDataFulfilled: () => LoadSessionData.getInstance().load(),
})
